import React, { useState } from "react";
import {
  Checkbox,
  Field,
  Radio,
  RadioGroup,
  Text,
} from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  usePlannerSourceSettingsText,
  usePlannerSourceSettingsViewModel,
  type PlannerMode,
} from "@onetasklist/shared/sources/planner";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface PlannerSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const PlannerSourceSettings: React.FC<PlannerSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = usePlannerSourceSettingsText(t);
  const vm = usePlannerSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [planQuery, setPlanQuery] = useState("");
  const [bucketsQuery, setBucketsQuery] = useState("");
  const [doneBucketQuery, setDoneBucketQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.microsoftplanner.sectionMode",
        )}
      >
        <Field orientation="horizontal" label={text.modeLabelText}>
          <RadioGroup
            value={vm.mode}
            onChange={(_, data) => vm.onSelectMode(data.value as PlannerMode)}
          >
            <Radio value="MyTasks" label={text.myTasksLabelText} />
            <Radio value="PlanTasks" label={text.planTasksLabelText} />
          </RadioGroup>
        </Field>

        {vm.isPlanMode && (
          <Field
            orientation="horizontal"
            label={text.planLabelText}
            hint={text.planHintText}
          >
            <SourceSingleTagPicker
              isLoading={vm.isLoadingPlans}
              selectedId={
                vm.selectedPlanId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedPlanId
              }
              selectedLabel={vm.selectedPlanTitle}
              query={planQuery}
              placeholder={text.planPlaceholderText}
              options={vm.plans.map((plan) => ({
                id: plan.id,
                label: plan.title || plan.id,
              }))}
              onQueryChange={setPlanQuery}
              onSelect={vm.onSelectPlan}
              clearSelectionText={text.planPlaceholderText}
            />
          </Field>
        )}
      </SettingsSectionCard>

      {vm.isPlanMode && vm.selectedPlanId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.microsoftplanner.sectionBuckets",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.bucketsLabelText}
            hint={text.bucketsHintText}
          >
            <SourceMultiTagPicker
              isLoading={vm.isLoadingBuckets}
              selectedIds={vm.selectedBucketIds}
              query={bucketsQuery}
              placeholder={text.bucketsPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.id,
                label: bucket.name || bucket.id,
              }))}
              onQueryChange={setBucketsQuery}
              onSelectionChange={vm.onSelectBuckets}
              minQueryLength={0}
              typeToSearchText={text.bucketsHintText}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneBucketLabelText}
            hint={text.doneBucketHintText}
            validationMessage={vm.bucketReferenceErrorText || undefined}
          >
            <SourceSingleTagPicker
              isLoading={vm.isLoadingBuckets}
              selectedId={
                vm.selectedDoneBucketId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneBucketId
              }
              selectedLabel={vm.selectedDoneBucketName}
              query={doneBucketQuery}
              placeholder={text.doneBucketPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.id,
                label: bucket.name || bucket.id,
              }))}
              onQueryChange={setDoneBucketQuery}
              onSelect={vm.onSelectDoneBucket}
              clearSelectionText={text.doneBucketPlaceholderText}
            />
          </Field>

          {vm.showCompleteWhenDone && (
            <Field
              orientation="horizontal"
              label={text.completeWhenDoneLabelText}
            >
              <Checkbox
                checked={vm.completeWhenDone}
                onChange={(_, data) =>
                  vm.onToggleCompleteWhenDone(!!data.checked)
                }
              />
            </Field>
          )}

          <Field orientation="horizontal" label={text.assignedToMeLabelText}>
            <Checkbox
              checked={vm.assignedToMe}
              onChange={(_, data) => vm.onToggleAssignedToMe(!!data.checked)}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {(vm.isLoadingPlans || vm.isLoadingBuckets) && (
        <Text>{text.loadingText}</Text>
      )}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
      {vm.isPlanMode && <Text>{text.assignedToMeHintText}</Text>}
      {vm.showCompleteWhenDone && <Text>{text.completeWhenDoneHintText}</Text>}
    </>
  );
};
